<template>
  <Teleport to="body">
    <transition name="workspace-wizard-modal-fade">
      <div
        v-if="modelValue"
        class="workspace-wizard-modal"
        role="presentation"
        @click="requestClose"
      >
        <div
          ref="dialog"
          class="glass-surface workspace-wizard-modal__dialog"
          :class="[`workspace-wizard-modal__dialog--${size}`, { 'has-aside': hasAside }]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
          @click.stop
          @keydown.esc.prevent="requestClose"
          @keydown.tab="trapFocus"
        >
          <span class="workspace-wizard-modal__ambient workspace-wizard-modal__ambient--one"></span>
          <span class="workspace-wizard-modal__ambient workspace-wizard-modal__ambient--two"></span>

          <header class="workspace-wizard-modal__header">
            <div class="workspace-wizard-modal__headcopy">
              <small v-if="kicker" class="workspace-kicker">{{ kicker }}</small>
              <h2 :id="titleId" class="workspace-wizard-modal__title">{{ title }}</h2>
              <p v-if="copy" class="workspace-wizard-modal__copy">{{ copy }}</p>
            </div>

            <button
              class="workspace-chip-button workspace-wizard-modal__close"
              type="button"
              :disabled="closeDisabled"
              :aria-label="closeLabel"
              @click="requestClose"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </header>

          <div v-if="steps.length" class="workspace-wizard-modal__steps" role="tablist" :aria-label="stepsLabel">
            <button
              v-for="(step, index) in steps"
              :key="`${titleId}-step-${index}`"
              type="button"
              class="workspace-wizard-modal__step"
              :class="stepClasses(index + 1, step)"
              :disabled="step.disabled || !clickableSteps"
              @click="$emit('step-select', index + 1)"
            >
              <span class="workspace-wizard-modal__step-index">{{ `${index + 1}`.padStart(2, '0') }}</span>
              <span class="workspace-wizard-modal__step-copy">
                <strong>{{ step.label }}</strong>
                <small v-if="step.helper">{{ step.helper }}</small>
              </span>
              <i v-if="currentStep > index + 1" class="fas fa-check workspace-wizard-modal__step-check" aria-hidden="true"></i>
            </button>
          </div>

          <div class="workspace-wizard-modal__body" :class="{ 'has-aside': hasAside }">
            <section class="workspace-wizard-modal__content">
              <slot />
            </section>

            <aside v-if="hasAside" class="workspace-wizard-modal__aside">
              <slot name="aside" />
            </aside>
          </div>

          <footer v-if="hasFooter" class="workspace-wizard-modal__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export default {
  name: 'WorkspaceWizardModal',

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    kicker: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    copy: {
      type: String,
      default: '',
    },
    steps: {
      type: Array,
      default: () => [],
    },
    currentStep: {
      type: Number,
      default: 1,
    },
    size: {
      type: String,
      default: 'lg',
    },
    clickableSteps: {
      type: Boolean,
      default: true,
    },
    closeDisabled: {
      type: Boolean,
      default: false,
    },
    closeLabel: {
      type: String,
      default: 'Cerrar modal',
    },
    stepsLabel: {
      type: String,
      default: 'Pasos del flujo',
    },
  },

  emits: ['close-request', 'step-select'],

  data() {
    return {
      titleId: `workspace-wizard-modal-title-${Math.random().toString(36).slice(2, 10)}`,
      previousActiveElement: null,
      bodyOverflow: '',
      htmlOverflow: '',
    }
  },

  computed: {
    hasAside() {
      return Boolean(this.$slots.aside)
    },

    hasFooter() {
      return Boolean(this.$slots.footer)
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.openModalState()
          return
        }

        this.closeModalState()
      },
    },
  },

  beforeUnmount() {
    this.closeModalState()
  },

  methods: {
    stepClasses(stepNumber, step) {
      return {
        'is-active': stepNumber === this.currentStep,
        'is-complete': stepNumber < this.currentStep,
        'is-disabled': Boolean(step && step.disabled),
      }
    },

    requestClose() {
      if (!this.closeDisabled) {
        this.$emit('close-request')
      }
    },

    getFocusableElements() {
      const dialog = this.$refs.dialog
      if (!dialog) {
        return []
      }

      return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
    },

    trapFocus(event) {
      const focusable = this.getFocusableElements()
      if (!focusable.length) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    },

    openModalState() {
      if (typeof document === 'undefined') {
        return
      }

      this.previousActiveElement = document.activeElement
      this.bodyOverflow = document.body.style.overflow
      this.htmlOverflow = document.documentElement.style.overflow
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'

      this.$nextTick(() => {
        const focusable = this.getFocusableElements()
        const dialog = this.$refs.dialog
        const target = focusable.find((element) => element.classList && element.classList.contains('workspace-wizard-modal__close'))

        if (target && typeof target.focus === 'function') {
          target.focus({ preventScroll: true })
          return
        }

        if (dialog && typeof dialog.focus === 'function') {
          dialog.focus({ preventScroll: true })
        }
      })
    },

    closeModalState() {
      if (typeof document === 'undefined') {
        return
      }

      document.body.style.overflow = this.bodyOverflow || ''
      document.documentElement.style.overflow = this.htmlOverflow || ''

      if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
        this.previousActiveElement.focus({ preventScroll: true })
      }

      this.previousActiveElement = null
    },
  },
}
</script>

<style scoped>
.workspace-wizard-modal {
  position: fixed;
  inset: 0;
  z-index: 1900;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(3, 8, 14, 0.68);
  backdrop-filter: blur(18px) saturate(145%);
  -webkit-backdrop-filter: blur(18px) saturate(145%);
}

.workspace-wizard-modal__dialog {
  position: relative;
  width: min(960px, calc(100vw - 32px));
  max-height: min(94vh, 960px);
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  overflow: hidden;
  border-radius: 26px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: linear-gradient(180deg, rgba(15, 19, 28, 0.99), rgba(11, 14, 21, 0.995));
  box-shadow: 0 28px 72px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(255, 255, 255, 0.03);
  outline: none;
  overscroll-behavior: contain;
}

.workspace-wizard-modal__dialog::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(188, 240, 236, 0.64), rgba(218, 232, 255, 0.52), transparent);
  pointer-events: none;
}

.workspace-wizard-modal__dialog--lg {
  width: min(860px, calc(100vw - 32px));
}

.workspace-wizard-modal__dialog--xl {
  width: min(1080px, calc(100vw - 32px));
}

.workspace-wizard-modal__dialog--full {
  width: min(1180px, calc(100vw - 24px));
  max-height: min(96vh, 1020px);
}

.workspace-wizard-modal__ambient {
  display: none;
}

.workspace-wizard-modal__ambient--one {
  top: 36px;
  left: 34px;
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, rgba(90, 209, 197, 0.28), transparent 72%);
}

.workspace-wizard-modal__ambient--two {
  right: 5%;
  bottom: 10%;
  width: 240px;
  height: 240px;
  background: radial-gradient(circle, rgba(211, 230, 248, 0.14), transparent 74%);
}

.workspace-wizard-modal__header,
.workspace-wizard-modal__footer {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  padding: 22px 24px 0;
}

.workspace-wizard-modal__headcopy {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.workspace-wizard-modal__title {
  margin: 0;
  color: var(--text-main);
  font-family: 'Syne', sans-serif;
  font-size: clamp(1.55rem, 1.28rem + 0.75vw, 2.2rem);
  line-height: 1.06;
  letter-spacing: -0.035em;
}

.workspace-wizard-modal__copy {
  margin: 0;
  max-width: 640px;
  color: rgba(213, 221, 234, 0.76);
  line-height: 1.55;
}

.workspace-wizard-modal__close {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  min-height: 42px;
  padding: 0;
  border-radius: 12px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.04);
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.workspace-wizard-modal__close:hover {
  border-color: rgba(196, 206, 228, 0.2);
  background: rgba(255, 255, 255, 0.07);
}

.workspace-wizard-modal__steps {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 8px;
  padding: 16px 24px 0;
}

.workspace-wizard-modal__step {
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  min-height: 68px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.035);
  color: var(--text-main);
  text-align: left;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease, opacity 180ms ease;
}

.workspace-wizard-modal__step:not(:disabled):hover {
  transform: translateY(-1px);
  border-color: rgba(188, 240, 236, 0.18);
}

.workspace-wizard-modal__step.is-active {
  transform: translateY(-1px);
  border-color: rgba(95, 216, 204, 0.2);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.035));
  box-shadow: 0 18px 34px rgba(2, 8, 14, 0.24), 0 0 0 1px rgba(90, 209, 197, 0.08);
}

.workspace-wizard-modal__step.is-complete {
  border-color: rgba(160, 171, 198, 0.14);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.025));
}

.workspace-wizard-modal__step.is-disabled {
  opacity: 0.48;
}

.workspace-wizard-modal__step-index,
.workspace-wizard-modal__step-check {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.workspace-wizard-modal__step-index {
  background: linear-gradient(135deg, rgba(240, 244, 250, 0.98), rgba(169, 183, 202, 0.92));
  color: #18212c;
  font-size: 0.84rem;
  font-weight: 800;
  box-shadow: 0 16px 26px rgba(0, 0, 0, 0.22);
}

.workspace-wizard-modal__step-check {
  color: #091114;
  background: linear-gradient(135deg, rgba(197, 245, 239, 0.94), rgba(120, 214, 208, 0.92));
}

.workspace-wizard-modal__step-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.workspace-wizard-modal__step-copy strong {
  color: var(--text-main);
  font-size: 0.98rem;
}

.workspace-wizard-modal__step-copy small {
  color: rgba(213, 221, 234, 0.68);
}

.workspace-wizard-modal__body {
  position: relative;
  z-index: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  padding: 18px 24px 22px;
  overflow: hidden;
}

.workspace-wizard-modal__body.has-aside {
  grid-template-columns: minmax(0, 1.55fr) minmax(300px, 0.9fr);
}

.workspace-wizard-modal__content,
.workspace-wizard-modal__aside {
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
  padding-bottom: 36px;
  overscroll-behavior: contain;
}

.workspace-wizard-modal__content {
  display: grid;
  gap: 14px;
}

.workspace-wizard-modal__content > * {
  animation: workspace-wizard-modal-rise 240ms ease both;
}

.workspace-wizard-modal__content > *:nth-child(2) {
  animation-delay: 35ms;
}

.workspace-wizard-modal__content > *:nth-child(3) {
  animation-delay: 70ms;
}

.workspace-wizard-modal__content > *:nth-child(4) {
  animation-delay: 105ms;
}

.workspace-wizard-modal__aside {
  display: grid;
  align-content: start;
  gap: 16px;
}

.workspace-wizard-modal__footer {
  align-items: center;
  padding: 16px 24px 20px;
  border-top: 1px solid rgba(196, 206, 228, 0.08);
  background: linear-gradient(180deg, rgba(7, 12, 19, 0.04), rgba(7, 12, 19, 0.34));
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

@keyframes workspace-wizard-modal-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.workspace-wizard-modal-fade-enter-active,
.workspace-wizard-modal-fade-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.workspace-wizard-modal-fade-enter-from,
.workspace-wizard-modal-fade-leave-to {
  opacity: 0;
}

.workspace-wizard-modal-fade-enter-from .workspace-wizard-modal__dialog,
.workspace-wizard-modal-fade-leave-to .workspace-wizard-modal__dialog {
  transform: translateY(14px) scale(0.985);
}

@media (max-width: 1080px) {
  .workspace-wizard-modal__body.has-aside {
    grid-template-columns: 1fr;
  }

  .workspace-wizard-modal__aside {
    order: -1;
    overflow: visible;
  }
}

@media (max-width: 760px) {
  .workspace-wizard-modal {
    padding: 0;
  }

  .workspace-wizard-modal__dialog,
  .workspace-wizard-modal__dialog--lg,
  .workspace-wizard-modal__dialog--xl,
  .workspace-wizard-modal__dialog--full {
    width: 100vw;
    max-width: 100vw;
    height: 100dvh;
    min-height: 100vh;
    min-height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
  }

  .workspace-wizard-modal__header,
  .workspace-wizard-modal__footer {
    padding-left: 14px;
    padding-right: 14px;
  }

  .workspace-wizard-modal__header {
    align-items: flex-start;
  }

  .workspace-wizard-modal__steps,
  .workspace-wizard-modal__body {
    padding-left: 14px;
    padding-right: 14px;
  }

  .workspace-wizard-modal__steps {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    overflow: visible;
    padding-bottom: 4px;
    margin-right: 0;
    padding-right: 0;
  }

  .workspace-wizard-modal__step {
    min-height: 58px;
    min-width: 0;
    flex: none;
    padding: 12px;
  }

  .workspace-wizard-modal__step-copy small {
    display: none;
  }

  .workspace-wizard-modal__step-index,
  .workspace-wizard-modal__step-check {
    width: 28px;
    height: 28px;
    border-radius: 9px;
  }

  .workspace-wizard-modal__title {
    font-size: clamp(1.35rem, 6vw, 1.8rem);
    line-height: 1.08;
  }

  .workspace-wizard-modal__copy {
    font-size: 0.95rem;
    line-height: 1.45;
  }

  .workspace-wizard-modal__close {
    width: 38px;
    height: 38px;
    min-height: 38px;
  }

  .workspace-wizard-modal__content,
  .workspace-wizard-modal__aside {
    padding-bottom: 176px;
  }

  .workspace-wizard-modal__footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -16px 32px rgba(0, 0, 0, 0.22);
  }
}
</style>
